<template>
    <b-col sm="12" md="12">
        <b-row class="fields-editor">
          <b-col cols="12" class="fields-main-area">
              <!-- Live version warning -->
              <b-alert v-if="config.isLiveVersion" show variant="warning" class="mb-3 d-flex align-items-center">
                <b-icon icon="exclamation-triangle-fill" class="mr-2 flex-shrink-0"></b-icon>
                {{ $t('liveVersionFieldsWarning') }}
              </b-alert>

              <!-- Schema Management Section -->
              <b-card class="mb-3 schema-management-card">
                <div class="schema-management-content">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">
                      <b-icon icon="collection" class="mr-2"></b-icon>
                      {{ $t('manageSchemas') }}
                    </h6>
                    <div>
                      <b-button v-if="!config.disableSchemas && !config.isLiveVersion" size="sm" variant="outline-primary" @click="availableFieldsSidebarOpen = true" class="mr-2">
                        <b-icon icon="plus-square" class="mr-1"></b-icon>
                        {{ $t('availableFields') }}
                      </b-button>
                      <b-button v-if="!config.disableSchemas && !config.isLiveVersion" size="sm" variant="primary" @click="showAddSchemaModal">
                        <b-icon icon="plus" class="mr-1"></b-icon>
                        {{ $t('addSchema') }}
                      </b-button>
                    </div>
                  </div>
                  <div v-if="allSchemas.length > 0" class="schema-badges mt-3">
                    <b-badge 
                      v-for="schema in allSchemas" 
                      :key="schema"
                      variant="info"
                      class="mr-2 mb-2 schema-badge"
                      pill>
                      {{ schema || $t('default') }}
                      <b-button 
                        v-if="schema !== '' && !config.disableSchemas && !config.isLiveVersion"
                        variant="link" 
                        size="sm" 
                        class="p-0 ml-2 text-white"
                        @click="removeSchema(schema)">
                        <b-icon icon="x" font-scale="0.9"></b-icon>
                      </b-button>
                    </b-badge>
                  </div>
                </div>
              </b-card>

              <!-- Fields by schema -->
              <div class="fields-content">
                <div v-for="schema in allSchemas" :key="schema" class="schema-group mb-4">
                  <div class="schema-header mb-2 px-2 py-2">
                    <b-icon icon="collection" class="mr-2"></b-icon>
                    <strong>{{ schema || $t('default') }}</strong>
                  </div>
                  <draggable 
                    tag="div" 
                    :class="{'fields-container': true, 'fields-dropzone': draggingField}" 
                    :value="getFieldsForSchema(schema)"
                    :group="config.disableSchemas || config.isLiveVersion ? {name: 'fields', put: false, pull: false} : {name: 'fields', put: true, pull: false}"
                    :disabled="config.isLiveVersion"
                    @input="updateFieldsForSchema($event, schema)"
                  >
                    <div 
                      :key="field.extensionName ? `${field.extensionName}-${field.key}` : `${schema}-${field.key}`" 
                      v-for="field in getFieldsForSchema(schema)"
                      class="field-item mb-3"
                    >
                      <b-card 
                        class="field-card" 
                        :class="{'extension-field': field.extensionName, 'field-card-readonly': config.isLiveVersion}"
                        @click="field.extensionName || config.isLiveVersion ? null : editModal(field)"
                        no-body
                      >
                        <b-card-body class="p-3">
                          <div class="d-flex align-items-center">
                            <div class="field-icon mr-3">
                              <b-icon :icon="getFieldDefinition(field).icon" font-scale="1.5" />
                            </div>
                            <div class="flex-grow-1">
                              <div class="field-label font-weight-bold mb-1">
                                {{ $translateLabel(field.label, field.name) }}
                                <b-badge v-if="field.extensionName" variant="warning" class="ml-2" pill>
                                  <b-icon icon="puzzle" font-scale="0.8" class="mr-1"></b-icon>
                                  {{ field.extensionName }}
                                </b-badge>
                              </div>
                              <div class="field-type text-muted small">
                                {{ $t(getFieldDefinition(field).title) }}
                              </div>
                            </div>
                            <div v-if="!field.extensionName" class="field-handle ml-2">
                              <b-icon icon="grip-vertical" class="text-muted" />
                            </div>
                            <div v-else class="ml-2">
                              <b-icon icon="lock" class="text-muted" />
                            </div>
                          </div>
                        </b-card-body>
                      </b-card>
                    </div>
                  </draggable>
                  
                  <div v-if="!getFieldsForSchema(schema).length" class="empty-state text-center py-5">
                    <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
                    <p class="text-muted">{{ $t('noFieldsInThisSchema') }}</p>
                    <p class="text-muted small">{{ $t('dragFieldsFromRight') }}</p>
                  </div>
                </div>
              </div>
          </b-col>
        </b-row>

        <!-- Available Fields Sidebar -->
        <b-sidebar right v-model="availableFieldsSidebarOpen" width="400px" shadow no-close-on-backdrop title="">
          <template #header>
            <div class="d-flex align-items-center justify-content-between w-100">
              <h5 class="mb-0">
                <b-icon icon="plus-square" class="mr-2"></b-icon>
                {{ $t('availableFields') }}
              </h5>
              <b-button variant="link" size="sm" @click="availableFieldsSidebarOpen = false" class="text-muted">
                <b-icon icon="x" font-scale="1.5"></b-icon>
              </b-button>
            </div>
          </template>
          
          <div class="px-3 py-3">
            <DraggableFields 
              :group="config.disableSchemas ? { name: 'fields', pull: false, put: false } : { name: 'fields', pull: 'clone', put: false }" 
              :mutateClone="onClone" 
              @dragStart="draggingField = true" 
              @dragEnd="draggingField = false"
            />
          </div>
        </b-sidebar>

        <!-- Edit Field Sidebar -->
        <b-sidebar right v-model="editFieldModal.show" width="500px" shadow backdrop>
          <template #header>
            <div class="d-flex align-items-center w-100">
              <h5 class="mb-0">{{ $t('editField') }}</h5>
            </div>
          </template>
          
          <div class="px-3">
            <FieldEdit v-model="editFieldModal.field" v-if="editFieldModal.field" :fields="fields" :component="component" :config="config"></FieldEdit>
          </div>

          <template #footer>
              <div class="d-flex bg-light border-top align-items-center px-3 py-3" v-if="!config.isLiveVersion">
                  <b-button variant="success" @click="editField" class="mr-2">
                    <b-icon icon="check-circle" class="mr-1"></b-icon>
                    {{$t('save')}}
                  </b-button>
                  <b-button variant="outline-danger" @click="remove(editFieldModal.field)">
                    <b-icon icon="trash" class="mr-1"></b-icon>
                    {{$t('delete')}}
                  </b-button>
              </div>
              <div class="d-flex bg-light border-top align-items-center px-3 py-3" v-else>
                  <b-alert show variant="warning" class="mb-0 w-100 py-2">
                    <b-icon icon="lock-fill" class="mr-2"></b-icon>
                    {{ $t('liveVersionFieldsWarning') }}
                  </b-alert>
              </div>
          </template>
        </b-sidebar>

        <!-- Add Schema Modal -->
        <main-dialog v-model="addSchemaModal.show" :title="$t('addSchema')" @ok="addSchema">
          <b-form-group :label="$t('schemaName')" label-for="schema-name">
            <b-form-input 
              id="schema-name"
              v-model="addSchemaModal.name"
              :placeholder="$t('enterSchemaName')"
              @keyup.enter="addSchema"
            ></b-form-input>
          </b-form-group>
        </main-dialog>
      </b-col>
</template>

<script>

import fieldDefs from "@/components/identityFields/defsv2"
import fieldObj from "@/components/identityFields/field"
import IdentityFields from '@/components/identityFields/IdentityFields'
import Mutators from "./Mutators"
import Relationships from "./Relationships"
import Template from "./Template"
import Constraints from "./Constraints"
import Context from "./Context"
import { Switch as cSwitch } from '@coreui/vue'
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import _ from "lodash"
import Helptip from '../../elements/Helptip.vue'
import VSelect from 'vue-select'
import draggable from 'vuedraggable'
import Vue from 'vue'

// css
import Texti18n from "../../form/Texti18n.vue"
import DraggableFields from './DraggableFields.vue'
import FieldEdit from "./FieldEdit.vue"

export default {
  components: {
    MutatorsEditor: Mutators,
    RelationshipsEditor: Relationships,
    ConstraintsEditor: Constraints,
    ContextEditor: Context,
    cSwitch,
    InputField: Input,
    SelectField: Select,
    Helptip,
    VSelect,
    Texti18n,
    Template,
    IdentityFields,
    draggable,
    DraggableFields,
    FieldEdit
},
  props: {
      value: {
        type: Array,
        default: function() {
          return []
        }
      },
      config: {
        type: Object,
        default: function() {
          return {
            fieldDefs: fieldDefs,
            hideGroup: false,
            hideProtected: false,
            hideVisible: false,
            fieldSize: 4,
            custom: false,
            dependencyProtected: false,
            version: 0,
            disableSchemas: false
          }
        }
      },

      component: {
        Type: Object
      }
  },
  data: function() {
    return {
      autogenerateKey: true,
      errors: [],
      newFieldModal: {
        show: false,
        field: _.cloneDeep(fieldObj)
      },

      editFieldModal: {
        show: false,
        field: _.cloneDeep(fieldObj)
      },

      addSchemaModal: {
        show: false,
        name: ''
      },

      predefinedSchemas: [],

      draggingField: false,
      availableFieldsSidebarOpen: false
    }
  },
  watch: {
    "newFieldModal.field.name": function(val) {
      if (this.autogenerateKey) {
         this.$set(this.newFieldModal.field, 'key',  _.replace(_.toLower(val), /\s/g, "_"))
      }

      this.newFieldModal.field.name = _.startCase(_.toLower(val))
    },

    "editFieldModal.field.name": function(val) {
      this.editFieldModal.field.name = _.startCase(_.toLower(val))
    },

    "newFieldModal.field.key": function(val) {
      if (!this.autogenerateKey) {
        this.$set(this.newFieldModal.field, 'key',  _.trim(_.replace(_.replace(_.toLower(val), " ", "_"), "__", "_"), "_"))
      }
    },

    autogenerateKey: function(val) {
      if (val) {
        this.newFieldModal.field.key = _.replace(_.toLower(this.newFieldModal.field.name), /\s/g, "_")
      }
    }
  },
  computed: {
    availablePermissions() {
      let perms = ["public-app", "platform-app", "public-api"]

      let publicApps = this.$store.getters.getPublicApps || []
      publicApps.forEach(p => {
        perms.push(`public-app::${p.name}`)
      })

      let platformApps = this.$store.getters.getApps || []
      platformApps.forEach(p => {
        perms.push(`platform-app::${p.name}`)
      })

      return perms
    },

    fields: {
      set(val) {
        // Filter out extension fields before saving - they should only come from extensions
        const regularFields = val.filter(f => {
          return this.getFieldDefinition(f) && !f.isExtensionField
        })
        this.$emit('input', regularFields)
      },

      get() {
        const v = this.value || []

        const fields = v.filter(f => {
          return this.getFieldDefinition(f)
        }) || []

        // Add extension fields
        const extensionFields = this.getExtensionFields()

        return [...fields, ...extensionFields]
      }
    },

    allSchemas() {
      const schemas = new Set()
      schemas.add('') // Always include default schema
      
      // Add schemas from fields
      this.fields.forEach(field => {
        if (field.schema) {
          schemas.add(field.schema)
        }
      })
      
      // Add predefined schemas
      this.predefinedSchemas.forEach(schema => {
        schemas.add(schema)
      })
      
      return Array.from(schemas).sort()
    },

    fieldDefs() {
      if (this.config !== null) {
        if (this.config.fieldDefs) {
          return this.config.fieldDefs
        }
      }

      return fieldDefs
    },

    roles() {
      return this.$store.getters.getRoles.map(r => {
        return {
          label: r.name,
          value: r.uuid
        }
      })
    },

    modules() {
      return this.fieldDefs.modules.map(m => {
        return m.key
      })
    },

    types() {
      const module = this.fieldDefs.modules.find(m => {
        return m.key == this.editFieldModal.field.module
      })

      const { fields = [] } = module || {}

      return fields.map(f => {
        return f.key
      })
    }
  },
  methods: {
    showAddSchemaModal() {
      this.addSchemaModal.name = ''
      this.addSchemaModal.show = true
    },

    addSchema() {
      const schemaName = this.addSchemaModal.name.trim()
      
      if (!schemaName) {
        this.$bvToast.toast(this.$t('pleaseEnterSchemaName'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }

      if (this.predefinedSchemas.includes(schemaName)) {
        this.$bvToast.toast(this.$t('schemaAlreadyExists'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }

      this.predefinedSchemas.push(schemaName)
      this.addSchemaModal.show = false
      this.addSchemaModal.name = ''

      this.$bvToast.toast(this.$t('schemaAdded'), {
        title: this.$t('success'),
        variant: 'success'
      })
    },

    removeSchema(schema) {
      const hasFields = this.fields.some(field => field.schema === schema)
      
      if (hasFields) {
        this.$mainDialog.msgBoxConfirm(this.$t('schemaHasFieldsConfirm'), {
          title: this.$t('confirmRemoval'),
          okVariant: 'danger',
          okTitle: this.$t('remove'),
          cancelTitle: this.$t('cancel')
        }).then(value => {
          if (value) {
            // Remove schema from predefined schemas
            this.predefinedSchemas = this.predefinedSchemas.filter(s => s !== schema)
            // Remove all fields that belong to this schema
            this.fields = this.fields.filter(field => (field.schema || '') !== schema)
            
            this.$bvToast.toast(this.$t('schemaRemoved'), {
              title: this.$t('success'),
              variant: 'success'
            })
          }
        })
      } else {
        this.predefinedSchemas = this.predefinedSchemas.filter(s => s !== schema)
        this.$bvToast.toast(this.$t('schemaRemoved'), {
          title: this.$t('success'),
          variant: 'success'
        })
      }
    },

    getFieldsForSchema(schema) {
      return this.fields.filter(field => {
        return (field.schema || '') === schema
      })
    },

    updateFieldsForSchema(newFields, schema) {
      // Remove old fields for this schema (exclude extension fields)
      const otherFields = this.fields.filter(field => {
        return (field.schema || '') !== schema && !field.isExtensionField
      })
      
      // Set schema for all new fields and clear group (exclude extension fields)
      const updatedFields = newFields
        .filter(field => !field.isExtensionField)
        .map(field => {
          return {
            ...field,
            schema: schema,
            group: '' // Clear group since we don't use it anymore
          }
        })
      
      // Combine and emit (extension fields will be added back via getExtensionFields in the getter)
      this.fields = [...otherFields, ...updatedFields]
    },

    editModal(field) {
      field = _.cloneDeep(field)
      let fieldO = _.cloneDeep(fieldObj) 

      Vue.set(this.editFieldModal, "field", {
        ...fieldO,
        ...field
      })

      this.editFieldModal.show = true
      this.editFieldModal.fieldOld = field
    },

    onClone(original) {
      original.name = `Field${this.fields.length+1}`
      original.key = `field${this.fields.length+1}`
      // Don't set group or schema here - will be set by updateFieldsForGroupAndSchema when dropped

      return original
    },

    getFieldDefinition(field) {
      let def = this.fieldDefs.modules.find(m => {
        return m.key == field.module
      })

      if (!def) {
        return {
          icon: 'question-circle',
          title: field.type || 'unknown'
        }
      }

      let fieldDef = def.fields.find(f => {
        return f.key == field.type
      })

      if (!fieldDef) {
        return {
          icon: 'question-circle',
          title: field.type || 'unknown'
        }
      }

      return fieldDef
    },

     cloneField(field) {
       // reset keys so new name and key can be set
      let cField =  _.cloneDeep(field)
      cField.name = ""
      cField.key = ""

      this.newFieldModal.field = cField
      this.newFieldModal.show = true
      this.newFieldModal.fieldOld = cField
    },

    editField(modal) {
      let field = this.editFieldModal.field
      let oldField = this.editFieldModal.fieldOld

      let fields = this.fields.map(el => {
        if (el.key == oldField.key && (el.schema || '') == (oldField.schema || '')) {
          return field
        } else {
          return el
        }
      });

      if (this.checkForErrors()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsNotValid'))

        modal.preventDefault();

        return false
      }

      this.editFieldModal.show = false

      this.fields = fields
    },

    addNewField(modal) {
      if (this.checkForErrors()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsNotValid'))

        modal.preventDefault();

        return false
      }

      if (this.validateFields()) {
        this.$mainDialog.msgBoxOk(this.$t('fieldsAlreadyExist'))

        modal.preventDefault();

        return false
      }

      //console.log(fieldObj)
      this.fields.push(this.newFieldModal.field)
      this.newFieldModal.field = _.cloneDeep(fieldObj)

      this.$emit('input', this.fields)

      return true
    },

    addNewFieldAndClose(modal) {
      if (this.addNewField(modal)) {
        this.newFieldModal.show = false
      }
    },

    hideEditField(modal) {
      if (modal.trigger != 'cancel' && modal.trigger != 'ok') {

        if (JSON.stringify(this.editFieldModal.fieldOld) != JSON.stringify(this.editFieldModal.field)) {
          modal.preventDefault()

          this.$mainDialog.msgBoxOk(this.$t('unsavedChanges'));
        }
      }

      if (modal.trigger == 'cancel') {
        this.editFieldModal.field = this.editFieldModal.fieldOld
      }
    },

    remove(field) {
      // Prevent removing extension fields
      if (field.isExtensionField) {
        this.$bvToast.toast(this.$t('cannotRemoveExtensionField'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }
      
      this.$mainDialog.msgBoxConfirm('Are you sure?').then((value) => {
        if (value) {
          let fields = this.fields.filter(f => {
            return !(f.key == field.key && (f.schema || '') == (field.schema || '')) && !f.isExtensionField
          })

          this.editFieldModal.show = false
          this.$emit('input', fields)
        }
      })
    },

    newFieldModalShow() {
      this.newFieldModal.field = _.cloneDeep(fieldObj)
      this.newFieldModal.show = true
    },

    fieldError(error) {
      let found = this.errors.find((i, e) => {
          return i.field == error.field
      })

      if (found === undefined) {
          this.errors.push(error)
      } else {
          this.errors.map(e => {
              if (found.field == e.field)
                  e.error = error.error
          })
      }
    },

    checkForErrors() {
      // for some fcking weird reason there is no error when Name is set to empty string, it doesn't show that fields is required
      // even though Key field is same and functions properly, fck this stupid shit, this is workaround i dont have time in my life to fck with this nonsense
      if (this.newFieldModal.field.name == "" && this.editFieldModal.field.name == "") {
        return true
      }

      if (this.newFieldModal.field.type == "" && this.editFieldModal.field.type == "") {
        return true
      }

      return this.errors.find((i, e) => {
        return i.error
      })
    },

    validateFields() {
      return this.fields.find(f => {
        const sameKey = this.newFieldModal.field.key == f.key && (this.newFieldModal.field.schema || '') == (f.schema || '')
        const sameName = this.newFieldModal.field.name == f.name && (this.newFieldModal.field.schema || '') == (f.schema || '')
        return sameKey || sameName
      })
    },

    helpfulTip(field) {
      if (field.module == "table") {
        if (field.type == "custom") {
           return {id: "identityField.table.custom", type: "icon", title: "Did you know?", size: "lg"}
        }
      }
    },

    canDisplayRelationships(field) {
      let mod = field.module
      let type = field.type

      switch(mod) {
        case 'select': return type == 'relation';
        case 'table': return type == 'default';
        default: return false
      }
    },

    canDisplayTemplate(field) {
      let mod = field.module
      let type = field.type

      switch(mod) {
        default: return true
      }
    },

    formatFieldPermissions(field) {
      if (field.permissions) {
        return field.permissions
      }

      return '*'
    },

    getExtensionFields() {
      if (!this.component || !this.component.settings || !this.component.settings.extensions) {
        return []
      }

      const extensionFields = []
      const extensions = this.component.settings.extensions || []

      extensions.forEach(extension => {
        if (extension.is_active && extension.fields) {
          extension.fields.forEach(field => {
            extensionFields.push({
              ...field,
              extensionName: extension.name,
              isExtensionField: true
            })
          })
        }
      })

      return extensionFields
    }
  },

  mounted() {
    this.$store.dispatch('fetchRoles')
  }
}
</script>

<style lang="scss" scoped>
.fields-editor {
  display: flex;
  flex-wrap: wrap;
  
  .fields-main-area {
    ::v-deep .card {
      border: none;
      background: transparent;
    }
    
    ::v-deep .card-body {
      padding: 1.5rem 0;
    }
  }
}

.fields-container {
  min-height: 200px;
  padding: 1rem 0;
  transition: all 0.2s ease;

  &.fields-dropzone {
    background: rgba(var(--primary-rgb), 0.05);
    border: 2px dashed rgba(var(--primary-rgb), 0.3);
    border-radius: 8px;
    padding: 1rem;
  }
}

.field-item {
  .field-card {
    border: 1px solid var(--border-color);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: var(--card-bg);

    &.extension-field {
      cursor: default;
      background: repeating-linear-gradient(
        45deg,
        var(--card-bg),
        var(--card-bg) 10px,
        rgba(255, 193, 7, 0.05) 10px,
        rgba(255, 193, 7, 0.05) 20px
      );
      border-left: 3px solid #ffc107;
    }

    &.field-card-readonly {
      cursor: default;
      opacity: 0.75;
    }

    &:hover:not(.extension-field):not(.field-card-readonly) {
      border-color: transparent;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      transform: translateY(-1px);

      ::v-deep .card-body {
        background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
      }

      .field-icon {
        background: transparent;
        color: white;
      }

      .field-label,
      .field-type {
        color: white !important;
      }

      .field-handle {
        color: white;
      }
    }

    .field-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 6px;
      background: var(--input-bg);
      color: var(--text-muted);
      transition: all 0.15s ease;
    }

    .field-label {
      color: var(--text-color);
      font-size: 0.9rem;
      transition: color 0.15s ease;
    }

    .field-type {
      font-size: 0.75rem;
      color: var(--text-muted);
      transition: color 0.15s ease;
    }

    .field-handle {
      cursor: grab;
      opacity: 0;
      transition: all 0.15s ease;
      color: var(--text-muted);

      &:active {
        cursor: grabbing;
      }
    }

    &:hover .field-handle {
      opacity: 1;
    }
  }
}

.empty-state {
  padding: 3rem 1rem;
  color: var(--text-muted);
  
  p {
    margin-bottom: 0.5rem;
  }
}

.schema-group {
  border-left: 3px solid var(--primary);
  padding-left: 1rem;
  margin-left: 0.5rem;
  
  .schema-header {
    background: var(--input-bg);
    border-radius: 6px;
    font-size: 0.9rem;
    color: var(--text-color);
    display: flex;
    align-items: center;
  }
}

::v-deep .nav-pills {
  .nav-link {
    color: var(--text-muted);
    border-radius: 6px;
    padding: 0.5rem 1rem;
    transition: all 0.15s ease;

    &:hover {
      background: var(--hover-bg);
      color: var(--text-color);
    }

    &.active {
      background: var(--primary);
      color: white;
    }
  }
}

.schema-management-card {
  border: 1px solid var(--border-color);
  
  .schema-management-content {
    padding: 1.25rem;
  }
  
  h6 {
    color: var(--text-color);
    font-size: 0.95rem;
    font-weight: 600;
  }

  .schema-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .schema-badge {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
    display: inline-flex;
    align-items: center;
    
    button {
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
      line-height: 1;
      
      &:hover {
        opacity: 0.8;
      }
    }
  }
}
</style>